'use client';

import React, { useEffect, useState, useRef } from 'react';
import { IconPhone, IconUser } from '@tabler/icons-react';
import styles from '@/styles/CallModal.module.css';

interface OutgoingCallModalProps {
  targetName: string;
  targetProfilePicture: string | null;
  onCancel: () => void;
  onConnected: () => void;
  onDeclined: () => void;
  status: 'calling' | 'connecting' | 'declined' | 'no_answer';
}

const CALL_TIMEOUT_SECONDS = 30;

export function OutgoingCallModal({
  targetName,
  targetProfilePicture,
  onCancel,
  onConnected,
  onDeclined,
  status,
}: OutgoingCallModalProps) {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Track time elapsed
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeElapsed((prev) => prev + 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Handle status changes
  useEffect(() => {
    if (status === 'connecting') {
      const timeout = setTimeout(() => {
        onConnected();
      }, 1000);
      return () => clearTimeout(timeout);
    } else if (status === 'declined') {
      const timeout = setTimeout(() => {
        onDeclined();
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [status, onConnected, onDeclined]);

  const getStatusMessage = () => {
    switch (status) {
      case 'calling':
        return 'Ringing...';
      case 'connecting':
        return 'Connecting...';
      case 'declined':
        return 'Call Declined';
      case 'no_answer':
        return 'No Answer';
      default:
        return 'Calling...';
    }
  };

  const getStatusClass = () => {
    const classes = [styles.statusText];

    switch (status) {
      case 'declined':
      case 'no_answer':
        classes.push(styles.statusError);
        break;
      case 'connecting':
        classes.push(styles.statusConnecting);
        break;
      case 'calling':
        classes.push(styles.statusTextAnimated);
        break;
    }

    return classes.join(' ');
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.title}>Outgoing Call</span>
          {status === 'calling' && (
            <span className={styles.timer}>{timeElapsed}s</span>
          )}
        </div>

        <div className={styles.userInfo}>
          {targetProfilePicture ? (
            <img
              src={targetProfilePicture}
              alt={targetName}
              className={styles.avatar}
            />
          ) : (
            <div className={styles.avatarFallback}>
              <IconUser size={36} />
            </div>
          )}
          <p className={styles.userName}>{targetName}</p>
          <p className={getStatusClass()}>
            {getStatusMessage()}
          </p>
        </div>

        {(status === 'calling' || status === 'no_answer') && (
          <div className={styles.actions}>
            <button
              className={`${styles.actionButton} ${styles.cancelButton}`}
              onClick={onCancel}
            >
              <IconPhone size={20} style={{ transform: 'rotate(135deg)' }} />
              <span>Cancel</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
